import { useEffect, useState } from "react";

import {
  FUNDING_EXCHANGE_NAMES,
  formatFundingCountdown,
  formatFundingRate,
  fundingRateClass,
} from "@/lib/funding";
import type { FundingInfo, ServiceExchange } from "@/lib/prices";

function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

interface FundingRateProps {
  funding: FundingInfo | null;
  /** Named in the label when the venue was picked for the user (alerts). */
  exchange?: ServiceExchange | null;
  className?: string;
}

/**
 * Current funding rate of a perpetual and time to the next payment, from the
 * price service. Renders nothing when the market has no funding (spot).
 */
export function FundingRate({ funding, exchange, className }: FundingRateProps) {
  const now = useNow(!!funding?.nextFundingTime);
  if (!funding) return null;
  const venue = exchange ? FUNDING_EXCHANGE_NAMES[exchange] : undefined;
  const next =
    funding.nextFundingTime && funding.nextFundingTime > now
      ? formatFundingCountdown(funding.nextFundingTime, now)
      : null;
  return (
    <div className={className}>
      <p className="text-xs text-text-secondary mb-1">
        Funding{venue ? ` (${venue})` : ""}
      </p>
      <p className={`text-sm ${fundingRateClass(funding.rate)}`}>
        {formatFundingRate(funding.rate)}
        {funding.intervalHours ? (
          <span className="text-xs text-text-secondary">
            {" "}
            / {funding.intervalHours}h
          </span>
        ) : null}
      </p>
      {next && (
        <p className="text-xs text-text-secondary mt-0.5">Next in {next}</p>
      )}
    </div>
  );
}
